// ---------------------------------------------------------------------------
// Grader-type registry — the single lookup surface the rest of the GraderPage
// uses instead of hardcoded `graderType === '…'` / `creationMethod === '…'`
// branches. Adding a user-authorable grader type means: write a descriptor
// (label, capabilities, spec-mapping, Overview cards, create flow) and register
// it in `GRADER_TYPE_DESCRIPTORS` below.
// ---------------------------------------------------------------------------

import type { ReactNode } from 'react';

import type { GraderCreationMethod } from '../../../context/grader.interface.js';
import type { Grader } from '../../../hooks/useGraders/useGraders.interface.js';
import type { GraderType } from '../../../services/grader.interface.js';

import {
  FallbackOverviewNotice,
  FallbackOverviewSections,
} from './FallbackOverviewSections.js';
import type {
  GraderCreateOption,
  GraderTypeDescriptor,
  OverviewEditContext,
} from './graderType.types.js';
import { latencyGraderDescriptor } from './latency/latency.descriptor.js';
import { llmGraderDescriptor } from './llm/llm.descriptor.js';
import { numericGraderDescriptor } from './numeric/numeric.descriptor.js';

/**
 * Every grader type the client models in detail, keyed by discriminator. Types
 * the backend can persist but the client doesn't model yet (see
 * {@link GraderType}) simply have no entry — callers fall back gracefully.
 */
export const GRADER_TYPE_DESCRIPTORS: Partial<
  Record<GraderType, GraderTypeDescriptor>
> = {
  [llmGraderDescriptor.type]: llmGraderDescriptor,
  [numericGraderDescriptor.type]: numericGraderDescriptor,
  [latencyGraderDescriptor.type]: latencyGraderDescriptor,
};

/** Descriptor for a grader type, or undefined for unmodelled/unknown types. */
export function getGraderTypeDescriptor(
  type: GraderType | undefined,
): GraderTypeDescriptor | undefined {
  return type ? GRADER_TYPE_DESCRIPTORS[type] : undefined;
}

/**
 * Create options for every user-authorable in-page grader type, in the order
 * they should appear on the create-method picker.
 */
export function getCreatableGraderTypes(): GraderCreateOption[] {
  return Object.values(GRADER_TYPE_DESCRIPTORS)
    .map((descriptor) => descriptor.create)
    .filter((create): create is GraderCreateOption => create != null);
}

/** The create option whose in-page flow matches a chosen creation method. */
export function getCreateOptionByMethod(
  method: GraderCreationMethod,
): GraderCreateOption | undefined {
  return getCreatableGraderTypes().find((option) => option.method === method);
}

/** Whether the Evaluate tab applies to a grader type (default: false). */
export function graderTypeSupportsEvaluate(
  type: GraderType | undefined,
): boolean {
  return getGraderTypeDescriptor(type)?.supportsEvaluate ?? false;
}

/** Whether the grader type has no client descriptor but is still a real type
 *  (so the Overview should show the read-only fallback). */
function isUnmodelledType(judge: Grader): boolean {
  return (
    judge.graderType != null &&
    getGraderTypeDescriptor(judge.graderType)?.OverviewSections == null
  );
}

/** Top-of-Overview notice for an unmodelled grader type, or null. Rendered
 *  above the shared Identity/Variables cards so the "not fully supported"
 *  context is the first thing seen. */
export function renderGraderOverviewNotice(judge: Grader): ReactNode {
  return isUnmodelledType(judge) ? (
    <FallbackOverviewNotice judge={judge} />
  ) : null;
}

/** Renders a grader type's Overview cards. Modelled types use their own
 *  sections; a real-but-unmodelled type falls back to a read-only view of its
 *  raw spec; a spec-less grader contributes nothing. */
export function renderGraderOverviewSections(
  judge: Grader,
  edit: OverviewEditContext,
): ReactNode {
  const descriptor = getGraderTypeDescriptor(judge.graderType);
  if (descriptor?.OverviewSections) {
    const Sections = descriptor.OverviewSections;
    return <Sections judge={judge} edit={edit} />;
  }
  // No descriptor but a known discriminator (or an unknown one the backend
  // sent) → show the generic read-only fallback so config isn't hidden.
  if (judge.graderType != null) {
    return <FallbackOverviewSections judge={judge} edit={edit} />;
  }
  return null;
}
